import React from 'react';
import zxcvbn from 'zxcvbn';
import {PasswordStrengthMeter, STRENGTH_LEVEL} from './PasswordStrengthMeter';

const MINIMUM_PASSWORD_LENGTH = 8;

function calculatePasswordStrengthLevel(
  password: string | undefined,
  userFields: string[],
): STRENGTH_LEVEL {
  if (!password) {
    return STRENGTH_LEVEL.BLANK;
  }

  if (password.length < MINIMUM_PASSWORD_LENGTH) {
    return STRENGTH_LEVEL.TOO_SHORT;
  }

  switch (zxcvbn(password, userFields).score) {
    case 0:
      return STRENGTH_LEVEL.WEAK;
    case 1:
      return STRENGTH_LEVEL.OK;
    case 2:
      return STRENGTH_LEVEL.GOOD;
    case 3:
    case 4:
      return STRENGTH_LEVEL.GREAT;
    default:
      throw new Error('Unexpected password score');
  }
}

function getPasswordSuggestion(password: string, userFields: string[]): string {
  const {
    feedback: {suggestions},
  } = zxcvbn(password, userFields);

  return suggestions[0];
}

/**
 * Reduce a user down to user fields
 */
function userFields(user?: Record<string, unknown>) {
  if (!user) {
    return [];
  }

  return Object.keys(user)
    .filter(
      key => key !== 'password' && typeof user[key] === 'string' && key in user,
    )
    .map(key => user[key] as string);
}

type Props = {
  user?: Record<string, unknown>;
  password: string | undefined;
};

export class PasswordStrengthCalculator extends React.Component<Props> {
  calculateStrength(value: string) {
    return calculatePasswordStrengthLevel(value, userFields(this.props.user));
  }

  validate(value: string) {
    const strength = this.calculateStrength(value);
    if (strength < STRENGTH_LEVEL.OK) {
      return getPasswordSuggestion(value, userFields(this.props.user));
    }
  }

  render() {
    return (
      <PasswordStrengthMeter
        level={this.calculateStrength(this.props.password || '')}
      />
    );
  }
}
